export interface Collection {
  id: string;
  slug: string;
  name: string;
  type: string;
  description: string | null;
  coverImage: string | null;
  region: string | null;
  country: string | null;
  itemCount: number;
  completedCount: number;
  pct: number; // 0–100
  /** Hill lists: height range of the peaks (directory sorts by height, ATL-132). */
  minElevation?: number | null;
  maxElevation?: number | null;
  /** Set when the collection is exactly one trail: link straight to /trails/:slug. */
  trailSlug?: string | null;
  distanceKm?: number | null;
  /** Single-trail collections: km walked, distance-weighted (null signed out). */
  walkedKm?: number | null;
  /**
   * ATL-168: set on a companion list (Munro Tops -> "munros"). Shown as a
   * section of the parent's page, not as a tile of its own.
   */
  parentSlug?: string | null;
  /** Section heading on the parent's page ("Tops", "Outlying Fells"). */
  companionLabel?: string | null;
}

/** ATL-168: a related list with the viewer's progress (hill lists only). */
export interface CollectionFamilyProgress {
  slug: string;
  name: string;
  itemCount: number;
  completedCount: number;
  pct: number; // 0-100
}

export interface CollectionCompanion extends CollectionFamilyProgress {
  label: string;
}

export interface CollectionsResponse {
  collections: Collection[];
}

export interface CollectionItem {
  id: string;
  name: string;
  slug: string;
  collected: boolean;
  visits: number;
  lastVisit: string | null;
  elevation?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  wikiUrl?: string | null;
  category?: string | null;
  region?: string | null;
  /** Viewer's own latest check-in photo (landmark items only). */
  photoUrl?: string | null;
  distanceKm?: number | null;
  elevationGainM?: number | null;
  totalSections?: number | null;
  completedSections?: number | null;
  /** Trail items: fractional overall progress % (partial sections pro-rata). */
  progressPct?: number | null;
  coveragePct?: number | null;
  country?: string | null;
}

export interface CollectionMeta {
  id: string;
  slug: string;
  name: string;
  type: string;
  description: string | null;
  cover_image: string | null;
  region: string | null;
  country: string | null;
}

export interface CollectionItemsResponse {
  collection: CollectionMeta;
  items: CollectionItem[];
  /** ATL-168: the list this one sits beside (absent from older APIs). */
  parent?: CollectionFamilyProgress | null;
  /** ATL-168: lists shown as sections of this one (absent from older APIs). */
  companions?: CollectionCompanion[];
}

/** ATL-168: companion lists are shown inside their parent, not as tiles. */
export function isCompanionCollection(c: { parentSlug?: string | null }): boolean {
  return c.parentSlug != null;
}

/** "Tops" reads as a common noun ("52 tops"); "Outlying Fells" keeps its capitals. */
export function companionLabelText(label: string): string {
  return label.includes(" ") ? label : label.toLowerCase();
}

/**
 * ATL-168: the parent card's hint that its companions exist, from the full
 * GET /collections list: "+ 52 tops", or "+ 12 / 52 tops" once started.
 * Null when the collection has no companions.
 */
export function companionNote(
  slug: string,
  all: readonly Pick<Collection, "parentSlug" | "companionLabel" | "name" | "itemCount" | "completedCount">[],
): string | null {
  const parts = all
    .filter((k) => k.parentSlug === slug)
    .map((k) => {
      const label = companionLabelText(k.companionLabel ?? k.name);
      return k.completedCount > 0 ? `+ ${k.completedCount} / ${k.itemCount} ${label}` : `+ ${k.itemCount} ${label}`;
    });
  return parts.length ? parts.join(" · ") : null;
}
